import qs from 'qs';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { InjectedRouter } from 'react-router';
import useFetchPackageVersionProjects from './useFetchPackageVersionProjects';
import { useDebouncedCallback } from '../../../hooks';
import * as PackageInventoryStore from '../../../redux/packageInventory';
import { parseQueryParams } from '../../../helpers/packageInventory';
import type { RouterProps } from '../../../types';

/**
 * Takes the location.search and parses it out, so it can be
 * synced with the Redux state.
 *
 * @param {string} search - The router's location.search
 */
const parseSearchParams = (search: string) => {
  const parsed = qs.parse(search, {
    //  `search` usually starts with a "?", so this allows us to ignore that.
    ignoreQueryPrefix: true,
  });
  return parseQueryParams(parsed);
};

/**
 * Synchronize URL and Redux state to display accurate packages,
 * regardless of user interaction (filter selection or manual URL editing).
 *
 * @param {InjectedRouter} router - React Router's injected router.
 * @param {Location} location - window.location object.
 */
const useSyncVersionDetailsStateAndURLParams = ({
  router,
  location,
  params,
}: RouterProps & {
  router: InjectedRouter;
}): void => {
  const hydrateState = useFetchPackageVersionProjects(
    PackageInventoryStore.actions.hydrateVersionDetailsQueryStateAction, params.packageLocator, params.version,
  );
  const stateQueryString = useSelector(PackageInventoryStore.selectors.getVersionDetailsStateQueryString);

  const syncURLParams = useDebouncedCallback((pathname: string, queryString: string) => {
    router.replace({
      pathname,
      search: `?${queryString}`,
    });
  });

  // Update Redux state if URL query params change.
  useEffect(() => {
    hydrateState(parseSearchParams(location?.search ?? ''));
  }, []);

  // Update URL query params if Redux state changes.
  useEffect(() => {
    syncURLParams(location.pathname, stateQueryString);
  }, [stateQueryString]);
};

export default useSyncVersionDetailsStateAndURLParams;
